import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Retorna os cadastros individuais por profissional
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_cns - CNS do profissional
 *
 * @returns {Array}
 */
export async function getCadastrosIndividuais(cod_ibge, nu_cns) {
  try {
    const payload = {
      co_ibge: cod_ibge,
      cpf: nu_cns,
    }
    const { data } = await Vue.prototype.$api.post(
      '/app/obitos/profissional',
      payload
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros individuais por microárea
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da equipe
 * @param {int} micro_area - Micro área do profissional
 *
 * @returns {Array}
 */
export async function getCadastrosIndividuaisPorMicroArea(
  cod_ibge,
  nu_ine,
  micro_area
) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/obitos/microarea/${cod_ibge}/${nu_ine}/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros individuais todos de uma equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getCadastrosIndividuaisTodosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/obitos/equipe/${cod_ibge}/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Regra para pintar a cor da linha da tabela
 *
 * @param {*} item
 * @returns {String}
 */
export function printTableRow(item) {
  if (item.dt_obito != null && item.saiu != '1') {
    return 'red'
  } else {
    return 'green'
  }
}

/**
 * Filtra os registros classificados como vermelhos.
 * @param {Array} cadastros Lista de registros
 * @returns {Array} Registros com dados desatualizados
 */
export function exibirVermelhos(cadastros) {
  let filtro = cadastros.filter((item) => {
    if (item.dt_obito != null && item.saiu != '1') {
      return true
    } else {
      return false
    }
  })
  return filtro
}

/**
 * Filtra os registros classificados como verdes.
 * @param {Array} cadastros Lista de registros
 * @returns {Array} Registros com dados atualizados
 */
export function exibirVerdes(cadastros) {
  let filtro = cadastros.filter((item) => {
    if (item.dt_obito != null && item.saiu != '1') {
      return false
    } else {
      return true
    }
  })
  return filtro
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 */
export function generatePdf(cadastros, selectEquipe, microArea) {
  const titlePdf = 'Óbitos'
  const titlePdfSave = utils.methods.titlePdf(titlePdf, selectEquipe.no_equipe)
  const descriptionPdf = 'Cidadãos com óbito informado no cadastro'
  const headersPdf = [
    'NOME',
    'NASCIMENTO',
    'MÃE',
    'MA',
    'ÓBITO',
    'PROCEDIMENTO',
    'OUT. ATENDIMENTO',
    'ODONTO',
    'VISITA',
    'DOCUMENTO',
  ]

  const dataPdf = cadastros.map((item) => {
    const documentos = []
    if (item.nu_cpf) documentos.push(`CPF ${item.nu_cpf}`)
    if (item.nu_cns) documentos.push(`CNS ${item.nu_cns}`)

    return {
      no_cidadao: item.no_cidadao,
      dt_nascimento: utils.methods.formatDate(item.dt_nascimento),
      no_mae: item.no_mae,
      micro_area: item.micro_area,
      dt_obito: utils.methods.formatDate(item.dt_obito),
      dt_procedimento: item.dt_procedimento,
      dt_atendimento_med_enf: item.dt_atendimento_med_enf,
      dt_atendimento_odo: item.dt_atendimento_odo,
      dt_visita: item.dt_visita,
      documento: documentos.join('\n'),
      saiu: item.saiu,
    }
  })

  const table = utils.methods.createTablePdf(dataPdf)
  const doc = new jsPDF(utils.methods.encryptionPdf())

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64Brasao) => {
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64Brasao,
        titlePdf,
        descriptionPdf
      )

      const equipeInfo = [
        {
          label: 'Equipe',
          value: selectEquipe.no_esf_eap ?? selectEquipe.no_equipe,
        },
        { label: 'CNES', value: selectEquipe.nu_cnes },
        { label: 'INE', value: selectEquipe.ine_esf_eap },
        { label: 'Microárea', value: microArea },
      ]

      verticalPosition = utils.methods.bodyPdf(
        doc,
        equipeInfo,
        verticalPosition
      )

      doc.autoTable({
        head: [headersPdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 6,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 6,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: ({ row, section }) => {
          if (section === 'head') {
            doc.setTextColor(0, 0, 0)
          } else {
            const obito = row.raw[4]
            const saiu = row.raw[10]
            if (obito != null && saiu != '1') {
              doc.setTextColor(194, 10, 16)
            } else {
              doc.setTextColor(2, 99, 57)
            }
          }
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY)
      doc.save(`${titlePdfSave}.pdf`)

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}
